// Index/components/ChatInput.tsx
import type {FC} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {useConversationEvents} from '@containers/Dashboards/AgentChat/components/ConversationStreamProvider.tsx'
import {useConversationRun} from '@containers/Dashboards/AgentChat/components/conversationRun/ConversationRunProvider'
import {
  useCreateMessageAttachmentMutation,
  useDeleteMessageAttachmentMutation,
} from '@apis/aiCoreAPI.ts'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import {useChatDropTarget} from '@containers/Dashboards/AgentChat/components/ChatDropZone'
import AttachmentIcon from '@containers/Dashboards/AgentChat/components/messages/AttachmentIcon'
import {
  isAcceptedFile,
  isImageFile,
  maxChatBytesFor,
  pendingAttachmentKind,
  rejectionReason,
  type BatchRejection,
} from '@containers/Dashboards/AgentChat/attachments'
import {useDocumentFormats} from '@hooks/useDocumentFormats'
import type {AttachmentReadMode} from '@interfaces/alpinist/message'

type PendingAttachment = {
  key: string
  file: File
  /** Only images get a local preview; a spreadsheet shows a chip instead. */
  objectUrl: string | null
  status: 'uploading' | 'ready' | 'error'
  /** Set once the upload returns: how the agent will read this file. */
  readMode?: AttachmentReadMode
  attachmentId?: string
}

const revokePreviews = (attachments: PendingAttachment[]) =>
  attachments.forEach((a) => {
    if (a.objectUrl) {
      URL.revokeObjectURL(a.objectUrl)
    }
  })

interface Props {
  conversationId: string
  value: string
  onChange: (v: string) => void
  onSend: (text: string, attachmentIds?: string[]) => void
  disabled?: boolean
  isMobile?: boolean
}

const ChatInput: FC<Props> = ({
  conversationId,
  value,
  onChange,
  onSend,
  disabled,
  isMobile = false,
}) => {
  const [t] = useCustomTranslation('alpinist')
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [awaitingFinal, setAwaitingFinal] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  // The reason, plus the limit that applied to the offending file — a message
  // that names a number the user can check beats one that says "too large".
  const [rejected, setRejected] = useState<{reason: BatchRejection; limitMb: number} | null>(null)
  // What can be attached, and how many, comes from the backend (AB#5986). This
  // component used to carry a second copy of the allowlist, stricter than the
  // Knowledge Base's for no reason the user could see.
  const {formats, accept, maxAttachmentsPerMessage, conversationRetentionDays} =
    useDocumentFormats()
  const [uploadAttachment] = useCreateMessageAttachmentMutation()
  const [deleteAttachment] = useDeleteMessageAttachmentMutation()
  // Stop & steer (AB#6760): the composer stays open while Alpinist works, and
  // what is sent then is taken into the run. Off, the old lock below applies.
  const run = useConversationRun()
  const steer = run.enabled

  // Reset lock and pending attachments when conversation changes
  useEffect(() => {
    setAwaitingFinal(false)
    setAttachments((prev) => {
      revokePreviews(prev)
      return []
    })
  }, [conversationId])

  // Revoke preview URLs on unmount
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments
  useEffect(() => () => revokePreviews(attachmentsRef.current), [])

  const {subscribe, connected, reconnectNow} = useConversationEvents()

  // Subscribe to events for this conversation
  useEffect(() => {
    if (!subscribe) {
      return
    }
    const off = subscribe({
      onAgentToken: () => setAwaitingFinal(true),
      onToolUpdate: () => setAwaitingFinal(true),
      onMsgFinal: () => setAwaitingFinal(false),
      onError: () => setAwaitingFinal(false),
    })
    return off
  }, [subscribe])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const addFiles = useCallback(
    (files: File[]) => {
      // Uploads are side effects, so they must stay OUT of the setAttachments
      // updater (StrictMode double-invokes updaters → duplicate uploads); the
      // room check reads the ref for the same reason.
      const room = maxAttachmentsPerMessage - attachmentsRef.current.length
      const reason = rejectionReason(files, room, formats)
      const oversized = files.find(
        (file) => !isAcceptedFile(file, formats) && maxChatBytesFor(file, formats) > 0,
      )
      setRejected(
        reason
          ? {
              reason,
              limitMb: oversized
                ? Math.round(maxChatBytesFor(oversized, formats) / (1024 * 1024))
                : 0,
            }
          : null,
      )
      const accepted = files
        .filter((file) => isAcceptedFile(file, formats))
        .slice(0, Math.max(0, room))
      if (!accepted.length) {
        return
      }
      const entries = accepted.map((file) => ({
        key: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        objectUrl: isImageFile(file) ? URL.createObjectURL(file) : null,
        status: 'uploading' as const,
      }))
      setAttachments((prev) => [...prev, ...entries])
      entries.forEach((entry) => {
        uploadAttachment({conversation_id: conversationId, file: entry.file})
          .unwrap()
          .then((created) => {
            setAttachments((cur) =>
              cur.map((a) =>
                a.key === entry.key
                  ? {
                      ...a,
                      status: 'ready',
                      attachmentId: created.id,
                      readMode: created.read_mode,
                    }
                  : a,
              ),
            )
          })
          .catch(() => {
            setAttachments((cur) =>
              cur.map((a) => (a.key === entry.key ? {...a, status: 'error'} : a)),
            )
          })
      })
    },
    [conversationId, uploadAttachment, formats, maxAttachmentsPerMessage],
  )

  const removeAttachment = useCallback(
    (key: string) => {
      // The side effects run out here, not inside the setState updater: React
      // invokes an updater twice in development, which fired the delete twice
      // and made the second a 404.
      const target = attachments.find((a) => a.key === key)
      if (target?.objectUrl) {
        URL.revokeObjectURL(target.objectUrl)
      }
      // Take it back on the server too. Dropping the chip alone left the
      // upload behind — attached to no message, ingested, and answering
      // searches in this conversation for as long as it lives. Attach the
      // wrong file, notice, remove it, and it was still there (AB#5992).
      if (target?.attachmentId && conversationId) {
        void deleteAttachment({
          conversation_id: conversationId,
          attachment_id: target.attachmentId,
        })
          .unwrap()
          .catch(() => {
            // The chip is already gone and the file is not in the message.
            // Nothing useful to ask of the user here.
          })
      }
      setAttachments((prev) => prev.filter((a) => a.key !== key))
    },
    [attachments, conversationId, deleteAttachment],
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.files ?? [])
      if (files.some((file) => isAcceptedFile(file, formats))) {
        e.preventDefault()
        addFiles(files)
      }
    },
    [addFiles, formats],
  )

  // Drops are owned by the surrounding ChatDropZone — including drops on the
  // composer itself, which it sees in the capture phase. The composer used to
  // handle its own and stop propagation, which left the zone stuck showing the
  // drop overlay (AB#5804).
  useChatDropTarget(addFiles)

  // The notice is about the last drop, so it clears on the next one and after a
  // few seconds — it is feedback, not a state the user has to dismiss.
  useEffect(() => {
    if (!rejected) {
      return
    }
    const timer = setTimeout(() => setRejected(null), 6000)
    return () => clearTimeout(timer)
  }, [rejected])

  const readyIds = attachments
    .filter((a) => a.status === 'ready' && a.attachmentId)
    .map((a) => a.attachmentId as string)
  const allSettled = attachments.every((a) => a.status === 'ready')

  const doSend = (text: string) => {
    if (!connected) reconnectNow()
    onSend(text, readyIds.length ? readyIds : undefined)
    setAttachments((prev) => {
      revokePreviews(prev)
      return []
    })
    setAwaitingFinal(false)
    requestAnimationFrame(() => inputRef.current?.focus({preventScroll: true}))
  }

  // Attachments must all be uploaded (an errored one has to be removed first);
  // with at least one attachment, empty text is a valid send. While Alpinist
  // answers, a send waits for the answer -- unless stop & steer is on.
  const canSend =
    (!!value.trim() || readyIds.length > 0) && allSettled && !disabled && (steer || !awaitingFinal)

  // What a stop is doing, on hover or focus of its status line and button: a
  // soft stop is not instant, and the person should know why and how to force it.
  let stopHint: React.ReactNode = null
  if (steer && run.stopMode === 'soft') {
    stopHint = (
      <>
        {t('run_wrapping_up_hint')}
        {run.waitingFor ? (
          <Box component="span" sx={{display: 'block', mt: 0.5}}>
            {t('run_waiting_for', {what: run.waitingFor})}
          </Box>
        ) : null}
      </>
    )
  } else if (steer && run.finishingWrite) {
    stopHint = t('run_stopping_write_hint')
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Esc stops the run, as the Stop button does: soft first, hard on the
      // second press.
      if (e.key === 'Escape' && steer && run.busy) {
        e.preventDefault()
        e.stopPropagation()
        if (run.stopMode !== 'hard') {
          run.stop()
        }
        return
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (!canSend) {
          // The hint says Alpinist is still answering, which holds no send
          // back once stop & steer is on.
          if (!steer) {
            // show tooltip hint for 2 seconds
            setShowHint(true)
            setTimeout(() => setShowHint(false), 2000)
          }
          return
        }
        doSend(value.trim())
      }
    },
    [canSend, value, steer, run],
  )

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault()
        if (canSend) {
          doSend(value.trim())
        }
      }}
      display="flex"
      flexDirection="column"
      gap={0.75}
      p={1.5}
      pb={2}
      borderTop={1}
      borderColor="divider"
    >
      {rejected ? (
        <Typography variant="caption" color="error">
          {/* With no format list there is nothing true to say about types —
              "Only these file types can be attached: ." is worse than saying
              the list could not be loaded. */}
          {accept
            ? t(`attachment_rejected_${rejected.reason}`, {
                max: maxAttachmentsPerMessage,
                types: accept.replace(/,/g, ', '),
                limitMb: rejected.limitMb,
              })
            : t('attachment_formats_unavailable')}
        </Typography>
      ) : null}

      {attachments.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{mb: 0.5}}>
          {conversationRetentionDays > 0
            ? t('attachment_scope_expiring', {days: conversationRetentionDays})
            : t('attachment_scope_kept')}
        </Typography>
      )}

      {/* A file is never taken into a run that already works: said before the
          send, not only on the bubble after it (AB#6760). */}
      {steer && run.busy && attachments.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t('attachment_next_turn_hint')}
        </Typography>
      ) : null}

      {attachments.length > 0 && (
        <Box display="flex" gap={1} flexWrap="wrap">
          {attachments.map((a) => (
            <Box
              key={a.key}
              sx={{
                position: 'relative',
                width: a.objectUrl ? 52 : a.readMode === 'indexed' ? 220 : 150,
                height: 52,
                borderRadius: 1,
                overflow: 'hidden',
                border: 1,
                borderColor: a.status === 'error' ? 'error.main' : 'divider',
              }}
            >
              {a.objectUrl ? (
                <Box
                  component="img"
                  src={a.objectUrl}
                  alt={a.file.name}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: a.status === 'ready' ? 1 : 0.4,
                  }}
                />
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  height="100%"
                  pl={1}
                  pr={2.5}
                  sx={{opacity: a.status === 'ready' ? 1 : 0.4}}
                >
                  <AttachmentIcon kind={pendingAttachmentKind(a.file)} />
                  <Box minWidth={0}>
                    <Typography variant="caption" noWrap display="block" title={a.file.name}>
                      {a.file.name}
                    </Typography>
                    {/* A file that does not fit in the message is still read —
                        through the document tools. Saying so is the difference
                        between "indexed" and "ignored" (AB#5990). Short enough
                        to fit the card, with the why on hover: the truncated
                        version explained nothing and had no tooltip to fall
                        back on. */}
                    {a.readMode === 'indexed' && (
                      <Tooltip title={t('attachment_indexed_hint')}>
                        <Typography variant="caption" noWrap display="block" color="text.secondary">
                          {t('attachment_indexed')}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}
              {a.status === 'uploading' && (
                <CircularProgress
                  size={18}
                  sx={{position: 'absolute', top: 'calc(50% - 9px)', left: 'calc(50% - 9px)'}}
                />
              )}
              {a.status === 'error' && (
                <Tooltip title={t('attachment_upload_failed')}>
                  <ErrorOutlineIcon
                    color="error"
                    fontSize="small"
                    sx={{position: 'absolute', top: 'calc(50% - 10px)', left: 'calc(50% - 10px)'}}
                  />
                </Tooltip>
              )}
              <IconButton
                size="small"
                aria-label={t('attachment_remove')}
                onClick={() => removeAttachment(a.key)}
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  p: 0.25,
                  bgcolor: 'background.paper',
                  '&:hover': {bgcolor: 'background.default'},
                }}
              >
                {/* A bin, not an X. The button deletes the upload — it does
                    not dismiss a chip — and now that it really does delete it
                    server-side, an X undersold what happens. Same icon the
                    Knowledge Base's own delete uses. */}
                <DeleteOutlineIcon size={15} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* A soft stop lets Alpinist wrap up; a hard one ends reads at once but
          lets a write in flight finish first. The person should know which. */}
      {steer && run.stopping ? (
        <Tooltip title={stopHint ?? ''} placement="top-start">
          <Typography
            variant="caption"
            color="text.secondary"
            role="status"
            // Focusable while it explains itself, so the tooltip reaches keyboard users.
            tabIndex={stopHint ? 0 : undefined}
            sx={{alignSelf: 'flex-start', cursor: stopHint ? 'help' : undefined}}
          >
            {t(
              run.stopMode === 'soft'
                ? 'run_wrapping_up'
                : run.finishingWrite
                  ? 'run_stopping_write'
                  : 'run_stopping',
            )}
          </Typography>
        </Tooltip>
      ) : null}
      {steer && run.stopFailed ? (
        <Typography variant="caption" color="error">
          {t('run_stop_failed')}
        </Typography>
      ) : null}

      <Box display="flex" alignItems="center" gap={1}>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          hidden
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []))
            e.target.value = ''
          }}
        />
        <Tooltip title={t('attach_file')}>
          <span style={{alignSelf: 'center'}}>
            <IconButton
              size="small"
              aria-label={t('attach_file')}
              disabled={
                disabled || !formats.length || attachments.length >= maxAttachmentsPerMessage
              }
              onClick={() => fileInputRef.current?.click()}
            >
              <AttachFileOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip
          title={t('input_tooltip_agent_responding')}
          open={showHint}
          arrow
          placement="top"
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          <TextField
            fullWidth
            inputRef={inputRef}
            placeholder={
              steer && run.busy ? t('input_placeholder_running') : t('input_placeholder')
            }
            multiline
            maxRows={5}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            variant="outlined"
            slotProps={{
              input: {
                sx: {
                  fontSize: isMobile ? 16 : 14,
                  lineHeight: 1.4,
                  paddingTop: 1.25,
                  paddingBottom: 1.25,
                },
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {borderColor: 'text.disabled'},
              },
            }}
          />
        </Tooltip>

        {/* While a soft stop wraps up, the button escalates to a hard stop. */}
        {steer && run.busy && run.stopMode === 'soft' ? (
          <Tooltip title={stopHint ?? t('run_stop_now_tooltip')}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => run.stop()}
              startIcon={<CircularProgress size={12} color="inherit" />}
              sx={{alignSelf: 'center', whiteSpace: 'nowrap', flexShrink: 0, textTransform: 'none'}}
            >
              {t('run_stop_now')}
            </Button>
          </Tooltip>
        ) : null}
        {steer && run.busy && run.stopMode !== 'soft' ? (
          <Tooltip title={t('run_stop_tooltip')}>
            <span style={{alignSelf: 'center'}}>
              <IconButton
                size="small"
                aria-label={t('run_stop')}
                onClick={() => run.stop()}
                disabled={run.stopping}
                sx={{border: 1, borderColor: 'divider'}}
              >
                {run.stopping ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <StopRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : null}

        <IconButton type="submit" disabled={!canSend} size="small" aria-label={t('send')}>
          <SvgIcon>
            <defs>
              <linearGradient id="mbai-ai-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#0e9afa" />
                <stop offset="50%" stopColor="#8f3fd5" />
                <stop offset="100%" stopColor="#e100ff" />
              </linearGradient>
            </defs>
            <SendRoundedIcon
              fontSize="small"
              style={{fill: canSend ? 'url(#mbai-ai-gradient)' : 'currentColor'}}
            />
          </SvgIcon>
        </IconButton>
      </Box>
    </Box>
  )
}

export default ChatInput
